import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { allowedPrioritiesForCategory } from '@crm/shared'
import type { ClientCategory } from '@crm/shared'
import { patchItem } from '../../../shared/api/requestItems.api'
import { PriorityBadge } from '../../../shared/ui/Badge'
import type { RequestItemPriority } from '../../../shared/api/types'
import { patchItemInCache } from '../utils/patchItemInCache'

export const PRIORITY_LABELS_MAP: Record<string, string> = {
  HIGH: '🔴 Высокий',
  MEDIUM: '🟡 Средний',
  LOW: '⚪ Базовый',
}

export function PriorityCell({
  itemId,
  currentPriority,
  requestId,
  clientCategory,
}: {
  itemId: string
  currentPriority: RequestItemPriority
  requestId: string
  /** Категория клиента заявки — нужна чтобы скрыть недопустимые приоритеты (A не может иметь LOW). */
  clientCategory?: ClientCategory
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPriority = e.target.value as RequestItemPriority
    if (!newPriority || newPriority === currentPriority) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await patchItem(itemId, { priority: newPriority })
      patchItemInCache(queryClient, requestId, itemId, { priority: newPriority })
      queryClient.invalidateQueries({ queryKey: ['requests', requestId], refetchType: 'none' })
    } finally {
      setSaving(false)
      setEditing(false)
    }
  }

  if (editing) {
    const allowed = clientCategory
      ? allowedPrioritiesForCategory(clientCategory)
      : (['HIGH', 'MEDIUM', 'LOW'] as RequestItemPriority[])
    return (
      <select
        autoFocus
        defaultValue={currentPriority}
        onChange={handleChange}
        onBlur={() => setEditing(false)}
        disabled={saving}
        className="w-full text-xs border border-brand-300 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-brand-400"
      >
        {allowed.map((v) => (
          <option key={v} value={v}>
            {PRIORITY_LABELS_MAP[v]}
          </option>
        ))}
      </select>
    )
  }

  return (
    <div
      onClick={() => setEditing(true)}
      title="Клик — изменить приоритет"
      className="cursor-pointer"
    >
      <PriorityBadge priority={currentPriority} />
    </div>
  )
}
